import { createFileRoute, Link } from "@tanstack/react-router";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";

const slug = "setup-mcp-cursor-image";
const path = `/blog/${slug}`;

export const Route = createFileRoute(path)({
	component: Article,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: path });
		return {
			meta: [
				{
					title: "How to Set Up MCP Image Generation in Cursor - AgentFramer",
				},
				{
					name: "description",
					content:
						"A working how-to for wiring AgentFramer's MCP server to Cursor 0.45+. Real .cursor/mcp.json, .cursorrules defaults, tool-call payloads, and the gotchas nobody tells you about.",
				},
				{
					property: "og:title",
					content: "How to Set Up MCP Image Generation in Cursor",
				},
				{
					property: "og:description",
					content:
						"Step-by-step setup for AgentFramer MCP in Cursor, with config snippets, tool-call payloads, and gotchas.",
				},
				{ property: "og:image", content: ogImageUrl(`blog-${slug}`) },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "article" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl(`blog-${slug}`) },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: path }),
		};
	},
});

function Article() {
	return (
		<article className="space-y-10">
			<div className="space-y-2">
				<p className="text-xs text-muted-foreground/60">April 2026</p>
				<h1 className="text-2xl font-medium tracking-tight">
					How to set up MCP image generation in Cursor
				</h1>
				<p className="text-sm text-muted-foreground">
					Your launch post needs an OG image. You have three minutes before the
					standup. Here is the working path from "Cursor open" to "PNG in{" "}
					<code>public/og/launch.png</code>" without leaving the editor.
				</p>
			</div>

			<div className="space-y-4 text-sm leading-relaxed text-muted-foreground">
				<p>
					Cursor 0.45 added first-class MCP server support, which means an agent
					inside the editor can call any compliant tool server the same way it
					calls a file edit. AgentFramer ships an MCP endpoint with five tools
					wired to a managed image, video, and audio backend:{" "}
					<code>generate_image</code>, <code>generate_video</code>,{" "}
					<code>generate_audio</code>, <code>get_generation</code>, and{" "}
					<code>get_credits</code>. Once the connection is live, the agent
					treats media generation like any other capability. No SDK install, no
					Python sidecar, no per-provider key juggling.
				</p>
				<p>
					This is the full setup. Real config files, the JSON payload Cursor
					actually sends, the prompts that work, and the failure modes that eat
					the most time when you skip ahead.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					What you need
				</h2>
				<ul className="list-disc pl-5 space-y-1">
					<li>Cursor 0.45 or newer. MCP support shipped in 0.45.</li>
					<li>
						An AgentFramer workspace.{" "}
						<Link
							to="/signup"
							className="text-primary hover:text-primary/80 transition-colors"
						>
							Sign up here
						</Link>{" "}
						if you don't have one.
					</li>
					<li>A repo open in Cursor. Anything works.</li>
					<li>Three minutes.</li>
				</ul>

				<h2 className="text-base font-medium text-foreground pt-2">
					Step 1: copy your MCP URL
				</h2>
				<p>
					In the AgentFramer dashboard, the home tab shows a single MCP URL for
					your workspace. It looks like{" "}
					<code>https://mcp.agentframer.com/mcp/ws_…</code> with a token in the
					path. The token is workspace-scoped, so anyone with the URL can burn
					your credits. Treat it like a database password: never paste it into a
					public repo, a screenshot, or a Slack channel that gets indexed.
				</p>
				<p>
					Click the copy button. Copy the entire string, token included. The
					most common reason "Cursor can't connect" is a half-copied URL.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Step 2: add the server in Cursor
				</h2>
				<p>
					Open Cursor, then <strong>Settings → MCP → Add new MCP server</strong>
					. Cursor accepts both URL-based servers (HTTP MCP) and command-based
					servers (stdio). AgentFramer is URL-based, so choose that option.
				</p>
				<p>
					Give the server a name like <code>agentframer</code>, paste the URL,
					and save. Within a second or two, Cursor handshakes the server and
					reads the tool list. You should see a green dot next to the server
					name, and the five tools listed under it. If the dot stays red, jump
					to the gotchas at the bottom.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Step 3: scope it per-project (optional, recommended)
				</h2>
				<p>
					The Settings UI writes to your global config at{" "}
					<code>~/.cursor/mcp.json</code>. That works, but a per-repo config at{" "}
					<code>.cursor/mcp.json</code> is usually what you want, because it
					commits the connection alongside the project and keeps team members on
					the same tool surface. Cursor reads project-level config first and
					merges the global on top.
				</p>
				<p>
					Drop this in <code>.cursor/mcp.json</code>:
				</p>
				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">{`{
  "mcpServers": {
    "agentframer": {
      "url": "https://mcp.agentframer.com/mcp/ws_REPLACE_WITH_TOKEN"
    }
  }
}`}</pre>
				<p>
					Add <code>.cursor/mcp.json</code> to <code>.gitignore</code> if your
					token sits in it directly. A cleaner pattern: keep a checked-in
					template at <code>.cursor/mcp.example.json</code>, and have each
					teammate copy it and paste their own workspace URL. Workspace tokens
					are per-user, so this also gives you per-developer usage attribution
					in the dashboard.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Step 4: pin defaults with .cursorrules
				</h2>
				<p>
					An agent with five new tools and no guidance will pick the most
					expensive model on its first try. Cursor reads project rules from{" "}
					<code>.cursorrules</code> at the repo root, or from any{" "}
					<code>.mdc</code> file under <code>.cursor/rules/</code>. Use one to
					pin the model, set a budget ceiling, and describe how images should
					land in the repo.
				</p>
				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">{`# Image generation rules

When generating images via the agentframer MCP server:

- Default to FLUX schnell for drafts and exploration.
- Escalate to FLUX 1.1 pro only for finals or when I say "high quality".
- Never spend more than $1 per session without confirming with me.
- Save assets under public/og/ for OG images, public/img/ for everything else.
- After generation, write the URL to scripts/og.url before downloading.
- If a tool returns insufficient_credits, stop and ask. Do not retry.`}</pre>
				<p>
					You can read more about model trade-offs on the{" "}
					<Link
						to="/models"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						models page
					</Link>{" "}
					and credit economics on{" "}
					<Link
						to="/pricing"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						pricing
					</Link>
					. Two lines in <code>.cursorrules</code> is usually enough to keep the
					agent from drifting toward $0.20 generations when a $0.003 draft would
					do.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Step 5: generate something real
				</h2>
				<p>Open Cursor chat and paste a concrete brief. Try this:</p>
				<p className="text-foreground">
					"Generate a 1200×630 OG image for the launch post titled 'MCP-native
					media generation' on a dark background with a soft purple gradient.
					Save the URL to <code>scripts/og.url</code>, then download the file to{" "}
					<code>public/og/launch.png</code>."
				</p>
				<p>
					Behind the scenes, Cursor expands that into a tool call. The actual
					payload that hits the MCP server looks roughly like this:
				</p>
				<pre className="rounded-md bg-card p-4 text-xs text-foreground overflow-x-auto">{`{
  "name": "generate_image",
  "arguments": {
    "model": "flux-schnell",
    "prompt": "Launch post OG image, headline 'MCP-native media generation', dark background, soft purple gradient, clean editorial layout",
    "width": 1200,
    "height": 630,
    "seed": 42
  }
}`}</pre>
				<p>
					The tool returns a generation id and a URL. Cursor polls{" "}
					<code>get_generation</code> until the status flips to{" "}
					<code>succeeded</code>, then writes the URL to{" "}
					<code>scripts/og.url</code> and runs <code>curl</code> (or whatever
					your shell tooling is) to download the PNG into{" "}
					<code>public/og/launch.png</code>. You watch this happen in the chat.
					Total time on a draft model is usually under five seconds.
				</p>
				<p>
					Iterate by replying in the same thread: "tighten the gradient", "more
					contrast on the headline", "regenerate at FLUX 1.1 pro for the final".
					The agent reuses the seed and prompt context, so each revision is a
					small delta rather than a fresh roll.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Common gotchas
				</h2>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong>Half-copied URL.</strong> The token is part of the path, not
						a header. If Cursor shows a red dot or "failed to connect", re-copy
						the entire URL from the dashboard.
					</li>
					<li>
						<strong>"No tools available" in chat.</strong> Cursor caches the
						tool list at startup. After adding a server, fully quit and relaunch
						Cursor (Cmd-Q on macOS, not just close window).
					</li>
					<li>
						<strong>insufficient_credits.</strong> The tool returns a structured
						error. Have the agent run <code>get_credits</code> to confirm
						balance, then top up in the dashboard. Do not let the agent retry in
						a loop, set a rule against it.
					</li>
					<li>
						<strong>Project rules ignored.</strong> If <code>.cursorrules</code>{" "}
						is not picked up, check that it sits at the repo root and that you
						opened the repo as a Cursor workspace (not a single file).
					</li>
					<li>
						<strong>Token in git history.</strong> If you committed a token by
						accident, rotate it from the dashboard immediately. Old tokens are
						revoked on rotation.
					</li>
				</ul>

				<h2 className="text-base font-medium text-foreground pt-2">
					Beyond image
				</h2>
				<p>
					The same connection covers the rest of the surface. Ask Cursor to
					"generate a 6-second video of the OG image animating in" and it calls{" "}
					<code>generate_video</code>. Ask for a "30-second voiceover of the
					launch headline in a calm narrator voice" and it calls{" "}
					<code>generate_audio</code>. No second integration, no second token.
					The{" "}
					<Link
						to="/help/tool-call-patterns"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						tool-call patterns guide
					</Link>{" "}
					has end-to-end recipes for chained workflows like image-to-video and
					prompt-to-podcast.
				</p>

				<h2 className="text-base font-medium text-foreground pt-2">
					Where Cursor MCP image generation falls short
				</h2>
				<p>
					This setup is excellent for the 80 percent case: dev-time assets, OG
					images, draft mocks, README hero shots, blog inline figures, quick
					concept exploration. It is the wrong tool for three jobs.
				</p>
				<ul className="list-disc pl-5 space-y-1">
					<li>
						<strong>Heavy art direction.</strong> If your brief runs to a page,
						with reference images, character sheets, and per-shot composition
						notes, you want a dedicated tool with an inpainting canvas, layer
						control, and node-based pipelines. A chat thread is a poor surface
						for that workload.
					</li>
					<li>
						<strong>Brand-locked aesthetics.</strong> If every output has to
						match a tightly controlled visual system (specific fonts,
						illustration style, mascot proportions), you need finetuned models,
						brand-conditioning embeddings, and a designer in the loop. Cursor
						plus an off-the-shelf MCP model will drift on every third
						generation.
					</li>
					<li>
						<strong>Large catalog batches.</strong> If you are rendering 10,000
						product variants for an e-commerce catalog, do not run that through
						an editor chat. Hit the AgentFramer API or batch pipeline directly
						with structured inputs, idempotency keys, and a job queue. The
						MCP-in-Cursor flow is interactive by design and does not amortize
						well over thousands of calls.
					</li>
				</ul>
				<p>
					For everything else (the image you needed five minutes ago, in the
					editor where you already are) this is the shortest path. Wire it up
					once, pin sane defaults, and your agent stops being a code-only tool.
					For a deeper tour of the MCP surface, see the{" "}
					<Link
						to="/help/mcp"
						className="text-primary hover:text-primary/80 transition-colors"
					>
						MCP help docs
					</Link>
					.
				</p>
			</div>
		</article>
	);
}
